import woocommerce from '../../../dashboard/api/WooCommerce';

const state = () => ({
  connection: null,

  testResult: null,

  webhook: {
    enabled: false,
    webhookUrl: null,
    webhookId: null,
  },

  syncJobs: {},

  accountId: null,

  loadingTest: false,
  loadingConnect: false,
  loadingStatus: false,
  loadingWebhook: false,
  loadingSync: false,
  loadingRecovery: false,
  loadingPrepare: false,

  error: null,

  uiFlags: {
    isInitialized: false,
    isFetching: false,
  },

  wcProducts: {
    items: [],
    page: 0,
    size: 10,
    totalPages: 0,
    totalElements: 0,
    sortBy: 'createdAt',
    sortDirection: 'DESC',
    isLoading: false,
    error: null,
    syncStatus: 'UNKNOWN',
  },
});

export const getters = {
  connection: s => s.connection,
  isConnected: s => !!s.connection?.isConnected,
  webhookEnabled: s => !!(s.connection?.webhookEnabled || s.webhook.enabled),

  syncJobById: s => id => s.syncJobs?.[id] || null,
  latestSyncJobId: s => s.connection?.syncJobId || null,

  accountId: s => s.accountId,

  loadingTest: s => s.loadingTest,
  loadingConnect: s => s.loadingConnect,
  loadingStatus: s => s.loadingStatus,
  loadingWebhook: s => s.loadingWebhook,
  loadingSync: s => s.loadingSync,
  loadingRecovery: s => s.loadingRecovery,
  loadingPrepare: s => s.loadingPrepare,

  error: s => s.error,
  uiFlags: s => s.uiFlags,
  testResult: s => s.testResult,

  wcProducts: s => s.wcProducts,
  wcProductsItems: s => s.wcProducts.items,
  wcProductsMeta: s => {
    const {
      page,
      size,
      totalPages,
      totalElements,
      sortBy,
      sortDirection,
      isLoading,
      error,
    } = s.wcProducts;
    return {
      page,
      size,
      totalPages,
      totalElements,
      sortBy,
      sortDirection,
      isLoading,
      error,
    };
  },
};

export const mutations = {
  setError(s, e) {
    s.error = e || null;
  },

  setConnection(s, dto) {
    s.connection = dto || null;
  },

  setTestResult(s, r) {
    s.testResult = r || null;
  },

  setWebhook(s, w) {
    s.webhook = { ...s.webhook, ...(w || {}) };
  },

  setSyncJob(s, { jobId, data }) {
    s.syncJobs = { ...(s.syncJobs || {}), [jobId]: data };
  },

  setAccountId(s, id) {
    s.accountId = id || null;
  },

  // لودینگ‌ها
  setLoadingTest(s, v) {
    s.loadingTest = !!v;
  },
  setLoadingConnect(s, v) {
    s.loadingConnect = !!v;
  },
  setLoadingStatus(s, v) {
    s.loadingStatus = !!v;
  },
  setLoadingWebhook(s, v) {
    s.loadingWebhook = !!v;
  },
  setLoadingSync(s, v) {
    s.loadingSync = !!v;
  },
  setLoadingRecovery(s, v) {
    s.loadingRecovery = !!v;
  },
  setLoadingPrepare(s, v) {
    s.loadingPrepare = !!v;
  },

  setUiFlags(s, flags) {
    s.uiFlags = { ...s.uiFlags, ...(flags || {}) };
  },

  setWcProductsLoading(s, v) {
    s.wcProducts.isLoading = !!v;
  },
  setWcProductsError(s, e) {
    s.wcProducts.error = e || null;
  },
  setWcProductsParams(s, { page, size, sortBy, sortDirection } = {}) {
    if (page !== undefined) s.wcProducts.page = page;
    if (size !== undefined) s.wcProducts.size = size;
    if (sortBy !== undefined) s.wcProducts.sortBy = sortBy;
    if (sortDirection !== undefined) s.wcProducts.sortDirection = sortDirection;
  },
  setWcProductsList(s, { items, page, size, totalPages, totalElements }) {
    s.wcProducts.items = items || [];
    if (page !== undefined) s.wcProducts.page = page;
    if (size !== undefined) s.wcProducts.size = size;
    s.wcProducts.totalPages = totalPages ?? 0;
    s.wcProducts.totalElements = totalElements ?? (items?.length || 0);
  },
  setWcProductsStatus(s, status) {
    s.wcProducts.syncStatus = status || 'UNKNOWN';
  },
};

function normErr(e, fallback) {
  return (
    e?.response?.data?.message ||
    e?.response?.data?.error ||
    e?.message ||
    fallback
  );
}

function pickActiveDto(dataArr, getters) {
  if (!Array.isArray(dataArr) || !dataArr.length) return null;

  if (dataArr.length === 1) return dataArr[0];

  const activeConnection = getters.connection;
  if (activeConnection?.id) {
    const found = dataArr.find(
      dto =>
        String(dto.id) === String(activeConnection.id) ||
        String(dto.connectionId) === String(activeConnection.id)
    );
    if (found) return found;
  }

  const connected = dataArr.find(dto => dto.isConnected || dto.connected);
  if (connected) return connected;

  return dataArr[0];
}

const actions = {
  setAccountId({ state: s, commit }, accountId) {
    if (s.accountId === accountId) return;
    woocommerce.setAccountId?.(accountId);
    commit('setAccountId', accountId);
    commit('setConnection', null);
    commit('setWebhook', { enabled: false, webhookId: null, webhookUrl: null });
    commit('setUiFlags', { isInitialized: false });
  },

  async testConnection({ commit }, payload) {
    commit('setLoadingTest', true);
    commit('setError', null);
    try {
      if (!woocommerce.testConnection) {
        throw new Error('testConnection endpoint not available');
      }
      const { data } = await woocommerce.testConnection(payload);
      commit('setTestResult', data);
      return data;
    } catch (e) {
      const msg = normErr(e, 'Failed to test connection');
      commit('setError', msg);
      commit('setTestResult', { success: false, message: msg });
      return { success: false, message: msg };
    } finally {
      commit('setLoadingTest', false);
    }
  },

  async prepareWebhook({ commit }) {
    commit('setLoadingPrepare', true);
    commit('setError', null);
    try {
      const { data } = await woocommerce.prepareWebhook();
      return data;
    } catch (e) {
      const msg = normErr(e, 'Failed to prepare webhook');
      commit('setError', msg);
      throw e;
    } finally {
      commit('setLoadingPrepare', false);
    }
  },

  async connect({ commit, dispatch }, payload) {
    commit('setLoadingConnect', true);
    commit('setError', null);
    try {
      let {
        url,
        consumerKey,
        consumerSecret,
        botId,
        webhookUuid,
        webhookSecret,
      } = payload || {};

      if (!webhookUuid || !webhookSecret) {
        const { data: prep } = await woocommerce.prepareWebhook();
        webhookUuid = prep?.webhookUuid || prep?.uuid || webhookUuid;
        webhookSecret = prep?.webhookSecret || prep?.secret || webhookSecret;
        if (!webhookUuid || !webhookSecret) {
          throw new Error('Failed to prepare webhook credentials');
        }
      }

      const { data } = await woocommerce.connect({
        url,
        consumerKey,
        consumerSecret,
        botId,
        webhookUuid,
        webhookSecret,
      });

      await dispatch('fetchStatus');
      return data;
    } catch (e) {
      const msg = normErr(e, 'Failed to connect WooCommerce');
      commit('setError', msg);
      return { success: false, message: msg };
    } finally {
      commit('setLoadingConnect', false);
    }
  },

  async fetchStatus({ commit, state }) {
    if (state.loadingStatus) {
      return;
    }

    commit('setLoadingStatus', true);
    commit('setError', null);
    try {
      const { data: raw } = await woocommerce.getStatus();
      const dto = raw?.data || raw || null;

      if (!dto) {
        commit('setConnection', null);
        commit('setUiFlags', { isInitialized: true });
        return null;
      }

      const normalized = {
        id: dto.id,
        url: dto.url,
        isConnected: !!dto.isConnected,
        webhookEnabled: !!dto.webhookEnabled,
        createdAt: dto.createdAt,
        status: dto.status || 'UNKNOWN',
        productCount: dto.productCount || 0,
        message: dto.isConnected ? 'Connected' : 'Disconnected',
      };

      commit('setConnection', normalized);
      commit('setUiFlags', { isInitialized: true });
      return normalized;
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        'Failed to fetch connection status';
      commit('setError', msg);
      commit('setConnection', null);
      commit('setUiFlags', { isInitialized: true });
      return null;
    } finally {
      commit('setLoadingStatus', false);
    }
  },

  /** POST /webhook/enable */
  async enableWebhook({ commit }, { webhookUrl, events } = {}) {
    commit('setLoadingWebhook', true);
    commit('setError', null);
    try {
      if (!woocommerce.enableWebhook)
        throw new Error('webhook/enable endpoint not available');
      const { data } = await woocommerce.enableWebhook({ webhookUrl, events });
      commit('setWebhook', {
        enabled: !!data?.success,
        webhookUrl: data?.webhookUrl || webhookUrl,
        webhookId: data?.webhookId ?? null,
      });
      return data;
    } catch (e) {
      const msg = normErr(e, 'Failed to enable webhook');
      commit('setError', msg);
      throw e;
    } finally {
      commit('setLoadingWebhook', false);
    }
  },

  /** POST /webhook/disable */
  async disableWebhook({ commit }) {
    commit('setLoadingWebhook', true);
    commit('setError', null);
    try {
      if (!woocommerce.disableWebhook)
        throw new Error('webhook/disable endpoint not available');
      const { data } = await woocommerce.disableWebhook();
      commit('setWebhook', {
        enabled: false,
        webhookId: null,
        webhookUrl: null,
      });
      return data;
    } catch (e) {
      const msg = normErr(e, 'Failed to disable webhook');
      commit('setError', msg);
      throw e;
    } finally {
      commit('setLoadingWebhook', false);
    }
  },

  /** DELETE /connection */
  async deleteConnection({ commit }) {
    commit('setLoadingConnect', true);
    commit('setError', null);
    try {
      await woocommerce.deleteConnection();
      commit('setConnection', null);
      commit('setWebhook', {
        enabled: false,
        webhookId: null,
        webhookUrl: null,
      });
      return true;
    } catch (e) {
      const msg = normErr(e, 'Failed to delete connection');
      commit('setError', msg);
      return false;
    } finally {
      commit('setLoadingConnect', false);
    }
  },

  async fetchSyncJobStatus({ commit }, jobId) {
    if (!jobId) return null;
    commit('setError', null);
    try {
      const { data } = await woocommerce.getSyncJobStatus(jobId);
      commit('setSyncJob', { jobId, data });
      return data;
    } catch (e) {
      const msg = normErr(e, 'Failed to fetch sync status');
      commit('setError', msg);
      return null;
    }
  },

  /** POST /sync-job/{jobId}/retry */
  async retrySyncJob({ commit }, jobId) {
    commit('setLoadingSync', true);
    commit('setError', null);
    try {
      const { data } = await woocommerce.retrySyncJob(jobId);
      commit('setSyncJob', { jobId, data });
      return data;
    } catch (e) {
      const msg = normErr(e, 'Failed to retry sync job');
      commit('setError', msg);
      throw e;
    } finally {
      commit('setLoadingSync', false);
    }
  },

  async startSync({ commit, dispatch }) {
    commit('setLoadingSync', true);
    commit('setError', null);
    try {
      const { data } = await woocommerce.startSync?.();
      const jobId = data?.jobId || data?.id || null;
      if (jobId) {
        commit('setSyncJob', { jobId, data });
        await dispatch('fetchSyncJobStatus', jobId);
      }
      return data;
    } catch (e) {
      const msg = normErr(e, 'Failed to start sync job');
      commit('setError', msg);
      throw e;
    } finally {
      commit('setLoadingSync', false);
    }
  },

  async manualRecovery({ commit, dispatch }) {
    commit('setLoadingRecovery', true);
    commit('setError', null);
    try {
      const { data } = await woocommerce.manualRecovery();


      if (!data?.success) {
        const msg =
          data?.message || 'Manual recovery failed: unknown server response';
        commit('setError', msg);
        return { success: false, message: msg };
      }

      await dispatch('fetchStatus');

      return {
        success: true,
        message: data.message || 'Manual recovery completed successfully',
      };
    } catch (e) {
      const msg = normErr(e, 'Manual recovery failed');
      commit('setError', msg);
      return { success: false, message: msg };
    } finally {
      commit('setLoadingRecovery', false);
    }
  },
  async fetchProducts({ commit, state, getters }, payload = {}) {
    const page = payload.page ?? state.wcProducts.page ?? 0;
    const size = payload.size ?? state.wcProducts.size ?? 10;
    const sortBy = payload.sortBy ?? state.wcProducts.sortBy ?? 'createdAt';
    const sortDirection =
      payload.sortDirection ?? state.wcProducts.sortDirection ?? 'DESC';

    if (state.wcProducts.isLoading) {
      return;
    }

    if (
      !payload.force &&
      state.wcProducts.page === page &&
      state.wcProducts.size === size &&
      state.wcProducts.sortBy === sortBy &&
      state.wcProducts.sortDirection === sortDirection &&
      state.wcProducts.items.length > 0 &&
      !state.wcProducts.error
    ) {
      return;
    }

    commit('setWcProductsParams', { page, size, sortBy, sortDirection });
    commit('setWcProductsLoading', true);
    commit('setWcProductsError', null);
    commit('setUiFlags', { isFetching: true });

    try {
      const { data: raw } = await woocommerce.getProducts({
        page,
        size,
        sortBy,
        sortDirection,
      });

      const items =
        raw?.data?.items ||
        raw?.data?.content ||
        (Array.isArray(raw?.data) ? raw.data : []);
      const paginationDto = raw?.data?.pagination || raw?.pagination || {};
      const totalElements =
        raw?.data?.totalElements ?? paginationDto.totalElements ?? items.length;
      const totalPages =
        raw?.data?.totalPages ??
        paginationDto.totalPages ??
        (size > 0 ? Math.ceil(totalElements / size) : 0);
      const syncStatus = raw?.data?.status || raw?.status || 'UNKNOWN';

      commit('setWcProductsList', { items, page, size, totalPages, totalElements });
      commit('setWcProductsStatus', syncStatus);

      return { items, page, size, totalPages, totalElements, syncStatus };
    } catch (e) {
      const msg = normErr(e, 'Failed to fetch WooCommerce products');
      commit('setWcProductsError', msg);
      commit('setWcProductsList', {
        items: [],
        page,
        size,
        totalPages: 0,
        totalElements: 0,
      });
      commit('setWcProductsStatus', 'ERROR');
      return {
        items: [],
        page,
        size,
        totalPages: 0,
        totalElements: 0,
        error: msg,
        syncStatus: 'ERROR',
      };
    } finally {
      commit('setWcProductsLoading', false);
      commit('setUiFlags', { isFetching: false });
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
